import { setEnableKtznLang } from "@/framework/helper/language";
import router from "./router";
import store from "@/store";
import { i18n } from "@/framework/plugins/i18n";
import { JoinStatus } from "@/modules/staff/objects/group/JoinStatus";
import { GroupService } from "@/framework/api/Staff/P2P/GroupService";
import { ConversationService } from "@/framework/api/Staff/ChatService/ConversationService";
import { ErrorService } from "@/framework/errors/ErrorService";
import { UserService } from "@/modules/auth/services/UserService";

export type ConvoResponse = {
  id: string;
  name: string;
  conversation_type: string;
  description: string;
  icon: string;
  invite_approval_type: string;
  notification_range: string;
  is_anonymous_join: boolean;
  created_at: string;
  updated_at: string;
};

export const conversationGuard = async (to: any, from: any, next: () => void) => {
  if (!(await wasInitialized(true))) {
    // initializeされてなければ(device_idがなければ)、initialに飛ばす
    router.replace(`/${i18n.locale}/initial`);
  }

  const user = await new UserService().getCachedQcUser();
  if (user.isErr()) {
    console.error("Failed to get user info from cache", user.error);
    return;
  }

  const getConvoRes = await getConversation(to.params.conversationId);
  if (!getConvoRes) {
    router.replace(`/${i18n.locale}/conversations`);
    return;
  }
  if (getConvoRes.conversation_type === "group") {
    const group = await new GroupService().loadGroup(getConvoRes.id);
    if (group) {
      let ownMember = null;
      if (user) {
        ownMember = group.members.find((member: any) => {
          return member.accessId === user.value.username;
        });
      } else console.error("[ERROR] user returned as null in conversationGuard");
      if (ownMember && ownMember.joinStatus !== JoinStatus.Joined) {
        router.replace(`/${i18n.locale}/group/join/${group.id}`);
        return;
      }
    }
  }
  next();
};

const getConversation = async (convId: string) => {
  try {
    const res = await new ConversationService().getConversationShallow({
      id: convId,
    });
    if (res.isErr()) {
      await new ErrorService().handleError(res.error);
      return null;
    } else {
      return res.value;
    }
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const checkAppInitial = async (to: any, from: any, next: () => void) => {
  if (await wasInitialized()) {
    setEnableKtznLang(to);
    next();
  } else {
    router.replace(`/${i18n.locale}/initial`);
    return;
  }
};

export const checkNeedInitialize = async (to: any, from: any, next: () => void) => {
  // /initialにアクセスした場合のチェック
  if (await wasInitialized()) {
    // すでに初期化(vender_idを元にaccess_idが作成)されていたら会話一覧へ飛ばす
    router.replace(`/${i18n.locale}/conversations`);
  } else {
    next();
  }
};

export const checkActivationStart = async (to: any, from: any, next: () => void) => {
  // /activation/〜にアクセスした場合のチェック
  if (await wasInitialized()) {
    // すでにdeviceIdとの紐付けが完了していれば、そのままアクティベーションフローへ
  } else {
    try {
      // 紐付けがされてなければ、deviceIdを作成してサインアップ
      // await signUpWithDeviceId();
      console.error("Not supported");
    } catch (error) {
      // この場合どうすれば良いのか
      console.log("Error check activation start: ", error);
    }
  }
  next();
};

const wasInitialized = async (fromConversation = false) => {
  if (store.getters.appMode === "customer_service") {
    await new UserService().getCachedStaff();
  }

  const user = await new UserService().getCachedQcUser();
  if (user.isErr()) {
    console.error("Failed to get user info from cache", user.error);
    return;
  }

  if (user.value && !store.getters.isExpired) {
    if (fromConversation) {
      // 会話ページへのguradの場合は、profileが存在するだけでok(sign_up_atの値をみない)
      return !!user;
    }

    if (user.value && user.value.signUpAt) {
      return true;
    } else if (user.value && !user.value.signUpAt) {
      // profileはあるが、sign_up_atがない場合
      console.log("Setting no sign_up_at");
      return false;
    }
  }

  // userがない場合
  if (store.getters.deviceId) {
    //device_idがあればそれでsign-inしてみる
    console.log("There is device_id", store.getters.deviceId);
    try {
      const user = await new UserService().getCachedQcUser();
      if (user.isErr()) {
        console.error("Failed to get user info from cache", user.error);
        return;
      }

      if (fromConversation) {
        return !!user.value;
      }
      if (user.value && user.value.signUpAt) {
        return true;
      } else if (user.value && !user.value.signUpAt) {
        // profileはあるが、sign_up_atがない場合
        return false;
      }
    } catch (error) {
      console.log("Error ", error);
    }
  }

  // cognito userもdevice_idもなければまだ初期化されていないと判断
  return false;
};

export const checkIsStaff = async (to: any, from: any, next: () => void) => {
  if (!(await wasInitialized())) {
    router.replace(`/${i18n.locale}/initial`);
    return;
  }
  const user = await new UserService().getCachedQcUser();
  if (user.isErr()) {
    console.error("Failed to get user info from cache", user.error);
    return;
  }

  if (!user.value || user.value.userType !== "biz_staff") {
    router.replace(`/${i18n.locale}/conversations`);
  }
  next();
};

export const proceedToHotelAdminFunction = async (to: any, from: any, next: () => void) => {
  // direct to [lang_cd]/hotel_admin_function has user already been logged in
  const user = await new UserService().getCachedQcUser();
  if (user.isErr()) {
    console.error("Failed to get user info");
  }
  if (user.isOk() && user.value && user.value.userType === "biz_staff") {
    router.replace(`/${i18n.locale}/hotel_admin_function`);
    return;
  } else {
    next();
  }
};

export const checkIsAdmin = async (to: any, from: any, next: () => void) => {
  const user = await new UserService().getCachedQcUser();
  if (user.isErr()) {
    console.error("Failed to get user info in checkIsAdmin", user.error);
    if (!router.currentRoute.path.includes("signin")) router.replace(`/${i18n.locale}/signin/admin`);
    return;
  }

  const staff = await new UserService().getCachedStaff();
  if (staff.isErr()) {
    console.error("Failed to get staff info in checkIsAdmin", staff.error);
    if (!router.currentRoute.path.includes("signin")) router.replace(`/${i18n.locale}/signin/admin`);
    return;
  }

  if ((user.isOk() && user.value.userType !== "biz_staff") || staff.value.myself.role === "staff") {
    console.error("User type is not staff or staff does not has correct role");
    if (!router.currentRoute.path.includes("signin")) router.replace(`/${i18n.locale}/signin/admin`);
    return;
  }

  setEnableKtznLang(to);

  next();
};
